import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../components/Styles/MarketIntelligenceFeedWidget.css';

const MODULE_LABELS = {
  first_flows: 'FIRST FLOWS',
  zero_gex: '0 GEX',
  trend: 'TREND',
  eod_range: 'EOD RANGE',
  market: 'MARKET INTELLIGENCE',
  null: 'MARKET INTELLIGENCE',
};

const TRADING_MODULES = new Set([
  'first_flows',
  'zero_gex',
  'trend',
  'eod_range',
]);

const PRODUCER_LABELS = {
  first_flows_posting_agent: 'First Flows Agent',
  zero_gex_posting_agent: '0 GEX Agent',
  trend_posting_agent: 'Trend Agent',
  eod_range_posting_agent: 'EOD Range Agent',
  market_intelligence_organizer: 'Market Intelligence Organizer',
};

const STATUS_LABELS = {
  active: 'ACTIVE',
  watching: 'WATCHING',
  mixed: 'MIXED',
  no_trade: 'NO TRADE',
  completed: 'COMPLETE',
};

function cleanLabel(value) {
  if (!value && value !== 0) return '—';

  return String(value)
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bGex\b/g, 'GEX')
    .replace(/\bSpx\b/g, 'SPX')
    .replace(/\bEod\b/g, 'EOD')
    .replace(/\bAtm\b/g, 'ATM');
}

function getModuleLabel(post) {
  const raw =
    post?.source_module ||
    post?.display_payload?.module ||
    post?.display_payload?.module_label ||
    'market';

  return MODULE_LABELS[raw] || cleanLabel(raw).toUpperCase();
}

function getStatusLabel(post) {
  return (
    post?.display_payload?.status_label ||
    STATUS_LABELS[post?.status] ||
    cleanLabel(post?.status).toUpperCase()
  );
}

function getAgentLabel(post) {
  return PRODUCER_LABELS[post?.producer_name] || cleanLabel(post?.producer_name);
}

function getConfidence(post) {
  return (
    post?.display_payload?.confidence_label ||
    post?.display_payload?.confidence ||
    null
  );
}

function getBias(post) {
  return (
    post?.display_payload?.bias_label ||
    post?.display_payload?.bias ||
    null
  );
}

function getTimeET(value) {
  if (!value) return '—';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function hasDisplayValue(value) {
  if (value === null || value === undefined || value === '') return false;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized !== '' && normalized !== 'none' && normalized !== 'null';
  }

  return true;
}

function normalizeKeyLevels(keyLevels) {
  if (!keyLevels || typeof keyLevels !== 'object' || Array.isArray(keyLevels)) {
    return [];
  }

  return Object.entries(keyLevels)
    .filter(([, value]) => hasDisplayValue(value))
    .map(([key, value]) => ({
      label: cleanLabel(key),
      value: Array.isArray(value) ? value.join(', ') : String(value),
    }));
}

function normalizeCardSections(cardSections) {
  if (!cardSections) return [];

  if (Array.isArray(cardSections)) {
    return cardSections.map((section, index) => {
      const title = section.title || section.label || `Section ${index + 1}`;
      const raw =
        section.content ||
        section.text ||
        section.value ||
        section.items ||
        '';

      return {
        title: cleanLabel(title),
        items: Array.isArray(raw) ? raw : [raw],
      };
    });
  }

  if (typeof cardSections === 'object') {
    return Object.entries(cardSections).map(([key, value]) => ({
      title: cleanLabel(key),
      items: Array.isArray(value) ? value : [value],
    }));
  }

  return [];
}

function getWatchNext(post) {
  if (hasDisplayValue(post?.watch_next)) {
    return String(post.watch_next);
  }

  const sections = normalizeCardSections(post?.display_payload?.card_sections);
  const watchSection = sections.find(
    (section) => section.title.toLowerCase() === 'watch next'
  );

  if (!watchSection) return null;

  const firstItem = watchSection.items.find((item) => hasDisplayValue(item));

  if (!hasDisplayValue(firstItem)) return null;

  return typeof firstItem === 'object'
    ? JSON.stringify(firstItem)
    : String(firstItem);
}

function getPriorityClass(priority) {
  const number = Number(priority);

  if (number <= 1) return 'critical';
  if (number === 2) return 'high';
  if (number === 3) return 'medium';
  if (number >= 4) return 'low';

  return 'normal';
}

function normalizePost(post) {
  const displayPayload = post?.display_payload || {};
  const sourceModule = post?.source_module || 'market';
  const isTradingModule = TRADING_MODULES.has(sourceModule);

  return {
    id: post.id,
    createdAt: post.created_at,
    tradingDate: post.trading_date,
    moduleLabel: getModuleLabel(post),
    statusLabel: getStatusLabel(post),
    agentLabel: getAgentLabel(post),

    confidence: isTradingModule ? null : getConfidence(post),
    bias: isTradingModule ? null : getBias(post),

    headline: post.headline || 'Market Intelligence Update',
    body: post.body || '',

    summaryBullets: isTradingModule
      ? []
      : Array.isArray(post.summary_bullets)
        ? post.summary_bullets
        : [],

    keyLevels: normalizeKeyLevels(post.key_levels),

    watchNext: isTradingModule ? getWatchNext(post) : null,

    cardSections: isTradingModule
      ? []
      : normalizeCardSections(displayPayload.card_sections),

    tags: isTradingModule
      ? []
      : Array.isArray(displayPayload.tags)
        ? displayPayload.tags
        : [],

    priorityClass: getPriorityClass(post.priority),
    isActive: Boolean(post.is_active),
    sourceModule,
    isTradingModule,
  };
}

function MarketIntelligencePost({ post, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <article className={`misPost ${post.priorityClass} ${open ? 'open' : ''}`}>
      <button
        className="misPostToggle"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className="misPostTop">
          <div className="misPostIdentity">
            <span className="misModuleBadge">{post.moduleLabel}</span>

            {!post.isTradingModule && (
              <span className="misStatusBadge">{post.statusLabel}</span>
            )}

            <span className="misTime">{getTimeET(post.createdAt)} ET</span>
          </div>

          <span className="misChevron">{open ? '▲' : '▼'}</span>
        </div>

        <h3>{post.headline}</h3>

        {!post.isTradingModule && post.summaryBullets.length > 0 && (
          <div className="misQuickBullets">
            {post.summaryBullets.slice(0, 3).map((bullet, index) => (
              <div key={`${post.id}-quick-${index}`}>
                <span>•</span>
                <p>{bullet}</p>
              </div>
            ))}
          </div>
        )}
      </button>

      {open && (
        <div className="misPostBodyWrap">
          {post.body && <p className="misPostBody">{post.body}</p>}

          {!post.isTradingModule && (post.bias || post.confidence) && (
            <div className="misReadMeta">
              {post.bias && (
                <div>
                  <span>Bias</span>
                  <strong>{cleanLabel(post.bias)}</strong>
                </div>
              )}

              {post.confidence && (
                <div>
                  <span>Confidence</span>
                  <strong>{cleanLabel(post.confidence)}</strong>
                </div>
              )}
            </div>
          )}

          {post.keyLevels.length > 0 && (
            <div className="misKeyLevels">
              {post.keyLevels.map((level) => (
                <div key={`${post.id}-${level.label}`}>
                  <span>{level.label}</span>
                  <strong>{level.value}</strong>
                </div>
              ))}
            </div>
          )}

          {post.isTradingModule && post.watchNext && (
            <div className="misCardSections">
              <section>
                <h4>Watch Next</h4>
                <p>{post.watchNext}</p>
              </section>
            </div>
          )}

          {!post.isTradingModule && post.cardSections.length > 0 && (
            <div className="misCardSections">
              {post.cardSections.map((section, index) => (
                <section key={`${post.id}-section-${index}`}>
                  <h4>{section.title}</h4>

                  {section.items.map((item, itemIndex) => (
                    <p key={`${post.id}-section-${index}-${itemIndex}`}>
                      {typeof item === 'object'
                        ? JSON.stringify(item)
                        : String(item)}
                    </p>
                  ))}
                </section>
              ))}
            </div>
          )}

          {!post.isTradingModule && post.tags.length > 0 && (
            <div className="misTags">
              {post.tags.map((tag) => (
                <span key={`${post.id}-${tag}`}>{cleanLabel(tag)}</span>
              ))}
            </div>
          )}

          <div className="misPostFooter">
            <span>{post.agentLabel}</span>
          </div>
        </div>
      )}
    </article>
  );
}

export default function MarketIntelligenceFeedWidget({ posts = [] }) {
  const feedRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const normalizedPosts = useMemo(() => {
    return posts
      .map(normalizePost)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [posts]);

  const latestPost = normalizedPosts[normalizedPosts.length - 1];

  useEffect(() => {
    if (!autoScroll || !feedRef.current) return;

    feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [normalizedPosts.length, autoScroll]);

  const handleScroll = () => {
    if (!feedRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = feedRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    setAutoScroll(distanceFromBottom < 80);
  };

  return (
    <section className="misTerminal">
      <header className="misTerminalHeader">
        <div>
          <span className="misTerminalTitle">MARKET INTELLIGENCE</span>
          <span className="misTerminalDate">
            {latestPost?.tradingDate || '—'}
          </span>
        </div>

        <div className="misTerminalRight">
          <span className={autoScroll ? 'misLive on' : 'misLive'}>
            {autoScroll ? 'LIVE' : 'PAUSED'}
          </span>
        </div>
      </header>

      <div className="misTerminalFeed" ref={feedRef} onScroll={handleScroll}>
        {normalizedPosts.length === 0 ? (
          <div className="misNoPosts">
            <h3>Connecting to the Market Intelligence System</h3>
            <p></p>
          </div>
        ) : (
          normalizedPosts.map((post, index) => (
            <MarketIntelligencePost
              key={post.id}
              post={post}
              defaultOpen={index === normalizedPosts.length - 1}
            />
          ))
        )}
      </div>
    </section>
  );
}